import { TABLES } from '@/utils/constants';
import { getRecord, updateRecord } from '@/utils/helpers/data';
import { PATIENT_COLUMNS, Patient } from '@/utils/schema';
import { supabase } from '@/utils/supabase';

export type { Patient };
export const COLUMNS = PATIENT_COLUMNS;

/**
 * Creates a new patient record in the database.
 * @param patient - The patient data to insert.
 * @throws Will throw an error if the insertion fails.
 */
export const createPatient = async (patient: Patient): Promise<void> => {
  const { error } = await supabase.from(TABLES.PATIENTS).insert(patient);

  if (error) {
    throw new Error(error.message);
  }
};

/**
 * Updates an existing patient record in the database.
 * @param patientId - The ID of the patient to update.
 * @param patient - The updated patient data.
 * @returns The updated patient or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updatePatient = async ({
  patientId,
  patient
}: {
  patientId: string | null | undefined;
  patient: Patient;
}): Promise<Patient | null> => {
  if (!patientId) return null;
  return updateRecord<Patient>(TABLES.PATIENTS, COLUMNS.ID, patientId, patient);
};

/**
 * Retrieves a single patient by their ID.
 * @param patientId - The ID of the patient to retrieve.
 * @returns The requested patient or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getPatient = async (
  patientId: string | null | undefined
): Promise<Patient | null> => {
  if (!patientId) return null;
  return getRecord<Patient>(TABLES.PATIENTS, COLUMNS.ID, patientId);
};
